// `/reset?token=…` on the admin host: spec 15 §4 S1b and §3.1 step 7 (D-587),
// the screen a staff reset email's link lands on.
//
// It follows accept-invite.tsx, its twin, rule for rule:
//
//  1. THE TOKEN IS A CREDENTIAL. Read once into a ref, never rendered, never
//     stored, sent only to `POST /v1/platform/auth/password-reset/complete`,
//     and scrubbed from the address bar the moment it is read.
//  2. NO ORACLE. Unknown, used, superseded and expired links, and a link whose
//     holder may not use it now, all answer the one STAFF_RESET_INVALID, and
//     the screen shows one state for it, with the way on: ask for a new link.
//  3. THE SERVER JUDGES THE PASSWORD. The meter is guidance; only the
//     12-character floor and the confirm-match gate the button. A policy
//     refusal leaves the link usable, so the form stays.
//  4. NO SESSION. Success sends the member to sign in with the new password
//     AND their two-factor code: the link proved the mailbox, nothing more.

import { useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Banner } from "../../../components/toast";
import { Button } from "../../../components/button";
import { AdminAuthCard } from "../admin-shell";
import { completePasswordReset, PlatformError } from "../lib/platform-api";
import { setAdminFlash } from "../lib/flash";
import {
  estimateStrength,
  PASSWORD_MIN_LENGTH,
  strengthSteps,
  type PasswordStrength,
} from "../lib/password-guidance";

/** Read the token out of the URL and scrub it from the address bar. */
function takeTokenFromUrl(): string {
  const url = new URL(window.location.href);
  const token = url.searchParams.get("token") ?? "";
  if (token !== "") {
    url.searchParams.delete("token");
    window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}`);
  }
  return token;
}

const STRENGTH_KEY: Record<PasswordStrength, string> = {
  none: "admin.accept.strength.none",
  weak: "admin.accept.strength.weak",
  fair: "admin.accept.strength.fair",
  good: "admin.accept.strength.good",
  strong: "admin.accept.strength.strong",
};

export function AdminResetPasswordScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const tokenRef = useRef<string | null>(null);
  tokenRef.current ??= takeTokenFromUrl();
  const token = tokenRef.current;

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [reveal, setReveal] = useState(false);
  const [touched, setTouched] = useState(false);

  const strength = estimateStrength(password);
  const tooShort = password.length > 0 && password.length < PASSWORD_MIN_LENGTH;
  const mismatch = confirm.length > 0 && confirm !== password;
  const canSubmit = token !== "" && password.length >= PASSWORD_MIN_LENGTH && confirm === password;

  const reset = useMutation({
    mutationFn: () => completePasswordReset({ token, new_password: password }),
    onSuccess: () => {
      setAdminFlash("password_reset");
      void navigate({ to: "/login" });
    },
  });

  const toForgot = (
    <Button
      variant="primary"
      onClick={() => {
        void navigate({ to: "/forgot-password" });
      }}
      data-testid="admin-reset-request-new"
    >
      {t("admin.reset.requestNew")}
    </Button>
  );

  if (token === "") {
    return (
      <AdminAuthCard
        title={t("admin.reset.title")}
        lead={t("admin.reset.lead")}
        data-testid="admin-reset"
      >
        <Banner kind="bad" data-testid="admin-reset-no-token">
          {t("admin.reset.missingToken")}
        </Banner>
        <div className="admin-actions">{toForgot}</div>
      </AdminAuthCard>
    );
  }

  const error = reset.error;
  // The link is dead: one state, whatever the reason, with the way on.
  if (error instanceof PlatformError && error.code === "STAFF_RESET_INVALID") {
    return (
      <AdminAuthCard
        title={t("admin.reset.invalid")}
        lead={t("admin.reset.invalidHelp")}
        data-testid="admin-reset"
      >
        <Banner kind="bad" data-testid="admin-reset-invalid">
          {t("admin.reset.invalid")}
        </Banner>
        <div className="admin-actions">
          {toForgot}
          <Button
            size="sm"
            onClick={() => {
              void navigate({ to: "/login" });
            }}
            data-testid="admin-reset-to-login"
          >
            {t("admin.reset.goSignIn")}
          </Button>
        </div>
      </AdminAuthCard>
    );
  }

  const errorMessage = ((): string | undefined => {
    if (error === null) return undefined;
    if (error instanceof PlatformError && error.code === "PASSWORD_POLICY") {
      return t("admin.reset.weak");
    }
    return t("admin.reset.failed");
  })();

  return (
    <AdminAuthCard
      title={t("admin.reset.title")}
      lead={t("admin.reset.lead")}
      data-testid="admin-reset"
    >
      {errorMessage !== undefined ? (
        <Banner kind="bad" data-testid="admin-reset-error">
          {errorMessage}
          {error instanceof PlatformError && error.requestId !== undefined ? (
            <span className="admin-reqid">
              {" "}
              {t("admin.reset.requestId")}: <code>{error.requestId}</code>
            </span>
          ) : null}
        </Banner>
      ) : null}
      <form
        className="admin-form"
        data-testid="admin-reset-form"
        onSubmit={(e) => {
          e.preventDefault();
          setTouched(true);
          if (!canSubmit) return;
          reset.mutate();
        }}
      >
        <div className="ds-field">
          <label htmlFor="admin-reset-password">{t("admin.reset.password")}</label>
          <input
            id="admin-reset-password"
            className="ds-input"
            type={reveal ? "text" : "password"}
            autoComplete="new-password"
            spellCheck={false}
            autoCapitalize="off"
            value={password}
            data-testid="admin-reset-password"
            onChange={(e) => {
              setPassword(e.target.value);
            }}
            onBlur={() => {
              setTouched(true);
            }}
            aria-describedby="admin-reset-password-hint admin-reset-strength"
            required
          />
          <p className="admin-hint" id="admin-reset-password-hint">
            {t("admin.reset.passwordHint")}
          </p>
          <div
            className="admin-strength"
            id="admin-reset-strength"
            data-testid="admin-reset-strength"
          >
            <span className="admin-strength-lb">{t("admin.accept.strengthLabel")}</span>
            <span className="admin-strength-bar" aria-hidden="true">
              {[0, 1, 2, 3].map((i) => (
                <i key={i} className={i < strengthSteps(strength) ? "is-on" : undefined} />
              ))}
            </span>
            <span className="admin-strength-v">{t(STRENGTH_KEY[strength])}</span>
          </div>
          <p className="admin-hint">{t("admin.accept.strengthNote")}</p>
          {touched && tooShort ? (
            <p className="admin-err" role="alert" data-testid="admin-reset-too-short">
              {t("admin.reset.tooShort")}
            </p>
          ) : null}
        </div>

        <div className="ds-field">
          <label htmlFor="admin-reset-confirm">{t("admin.reset.confirm")}</label>
          <input
            id="admin-reset-confirm"
            className="ds-input"
            type={reveal ? "text" : "password"}
            autoComplete="new-password"
            spellCheck={false}
            autoCapitalize="off"
            value={confirm}
            data-testid="admin-reset-confirm"
            onChange={(e) => {
              setConfirm(e.target.value);
            }}
            required
          />
          {mismatch ? (
            <p className="admin-err" role="alert" data-testid="admin-reset-mismatch">
              {t("admin.reset.mismatch")}
            </p>
          ) : null}
        </div>

        <div className="admin-actions">
          <Button
            type="submit"
            variant="primary"
            busy={reset.isPending}
            disabled={!canSubmit}
            data-testid="admin-reset-submit"
          >
            {reset.isPending ? t("admin.reset.submitting") : t("admin.reset.submit")}
          </Button>
          <Button
            size="sm"
            onClick={() => {
              setReveal((v) => !v);
            }}
            data-testid="admin-reset-reveal"
          >
            {reveal ? t("admin.reset.hide") : t("admin.reset.show")}
          </Button>
        </div>
      </form>
    </AdminAuthCard>
  );
}
